import { useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { bridge } from '../bridge'
import './SwipeableRow.css'

type Action = {
  label: string
  onAction: () => void
  tint?: string
}

/// iOS-style swipe-to-reveal row. Horizontal drag pulls a destructive
/// action button in from the trailing edge; release past the threshold
/// fires it, otherwise snap back. Also supports an optional long-press
/// gesture (500ms hold, no motion) for a secondary action menu.
export function SwipeableRow({
  children,
  trailing,
  onLongPress,
}: {
  children: ReactNode
  trailing?: Action
  onLongPress?: () => void
}) {
  const [dx, setDx] = useState(0)
  const [open, setOpen] = useState(false)
  const startX = useRef(0)
  const pointerId = useRef<number | null>(null)
  const captured = useRef(false)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressFired = useRef(false)

  if (!trailing && !onLongPress) return <div className="swipe">{children}</div>

  const threshold = 72
  // Horizontal motion that elevates a press from "tap" to "swipe". Only
  // once we cross it do we claim pointer capture — before that, letting
  // the browser's click bubble up to the child button naturally.
  const dragStart = 6
  const longPressMs = 500

  function cancelLongPress() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    pointerId.current = e.pointerId
    startX.current = e.clientX
    captured.current = false
    longPressFired.current = false
    if (onLongPress) {
      longPressTimer.current = setTimeout(() => {
        longPressTimer.current = null
        longPressFired.current = true
        bridge.call('haptic.light').catch(() => {})
        onLongPress()
      }, longPressMs)
    }
  }
  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    if (pointerId.current !== e.pointerId) return
    // A hovering mouse (no button held) must never drive the row. Desktop has
    // no implicit pointer capture, so when a long-press opens a sheet the
    // pointerup can land on the overlay and never reach us, leaving pointerId
    // set. Without this the next hover resumes "dragging" the row (Firefox:
    // the trailing action re-reveals and tracks the cursor). Real drags — mouse
    // or touch — always report buttons >= 1, so this only bails on hover, and
    // it self-heals the leaked pointer.
    if (e.buttons === 0) {
      cancelLongPress()
      pointerId.current = null
      return
    }
    const delta = e.clientX - startX.current
    if (!captured.current) {
      if (Math.abs(delta) < dragStart) return
      // Any meaningful motion disqualifies a long-press.
      cancelLongPress()
      if (!trailing) return
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      captured.current = true
    }
    const next = open ? Math.min(0, -threshold + delta) : Math.min(0, delta)
    setDx(Math.max(-200, next))
  }
  function onPointerEnd(e: PointerEvent<HTMLDivElement>) {
    if (pointerId.current !== e.pointerId) return
    pointerId.current = null
    cancelLongPress()
    if (!captured.current) {
      if (longPressFired.current) {
        // Long-press consumed the gesture; swallow the click that would
        // otherwise fire on the child button.
        e.preventDefault()
      }
      return
    }
    captured.current = false
    const shouldOpen = dx < -threshold / 2
    setOpen(shouldOpen)
    setDx(shouldOpen ? -threshold : 0)
  }

  return (
    <div className="swipe">
      {trailing && (
        <div className="swipe__actions">
          <button
            className="swipe__action swipe__action--destructive"
            style={trailing.tint ? { background: trailing.tint } : undefined}
            onClick={() => {
              setOpen(false)
              setDx(0)
              trailing.onAction()
            }}
          >
            {trailing.label}
          </button>
        </div>
      )}
      <div
        className="swipe__content"
        style={{ transform: `translateX(${dx}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onClickCapture={(e) => {
          // When a long-press fires, React still queues the click that
          // follows the pointerup. Swallow it here so the child button's
          // onClick (which typically navigates) doesn't run on top of
          // the action sheet.
          if (longPressFired.current) {
            longPressFired.current = false
            e.preventDefault()
            e.stopPropagation()
          }
        }}
      >
        {children}
      </div>
    </div>
  )
}
